import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-950 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-amber-500" />
            <h1 className="text-3xl font-bold text-white">Política de Privacidad</h1>
          </div>
          <p className="text-gray-400">Última actualización: 5 de diciembre de 2024</p>
        </div>

        {/* Content */}
        <div className="bg-gray-900 rounded-2xl border border-gray-800 p-8 space-y-8 text-gray-300">

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Información del Responsable</h2>
            <p className="mb-2">
              <strong className="text-white">Responsable:</strong> Mª Angeles Morales Marquez
            </p>
            <p className="mb-2">
              <strong className="text-white">Marca comercial:</strong> Reserberry
            </p>
            <p className="mb-2">
              <strong className="text-white">Dirección:</strong> Calle Río Retortillo, Mijas, Málaga, España
            </p>
            <p className="mb-2">
              <strong className="text-white">Email de contacto:</strong>{' '}
              <a href="mailto:navora.citas@gmail.com" className="text-amber-400 hover:text-amber-300">
                navora.citas@gmail.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Datos que Recopilamos</h2>
            <p className="mb-4">
              Reserberry Sport recopila los siguientes datos personales para proporcionar nuestros servicios:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-white">Datos de cuenta:</strong> Nombre, apellidos, dirección de correo electrónico y contraseña (encriptada).</li>
              <li><strong className="text-white">Datos de contacto:</strong> Número de teléfono (opcional).</li>
              <li><strong className="text-white">Foto de perfil:</strong> Imagen que el usuario sube voluntariamente.</li>
              <li><strong className="text-white">Datos de uso:</strong> Historial de reservas, clases asistidas y preferencias.</li>
              <li><strong className="text-white">Datos de pago:</strong> Procesados de forma segura por Stripe. No almacenamos datos de tarjetas.</li>
              <li><strong className="text-white">Datos técnicos:</strong> Tipo de dispositivo, sistema operativo e información de errores (Crashlytics).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Finalidad del Tratamiento</h2>
            <p className="mb-4">Utilizamos sus datos para:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Gestionar su cuenta de usuario y autenticación.</li>
              <li>Permitir la reserva de clases y gestión de membresías.</li>
              <li>Procesar pagos y suscripciones.</li>
              <li>Enviar notificaciones relacionadas con sus reservas.</li>
              <li>Mejorar nuestros servicios y corregir errores técnicos.</li>
              <li>Cumplir con obligaciones legales.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Base Legal</h2>
            <p className="mb-4">El tratamiento de sus datos se basa en:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-white">Ejecución del contrato:</strong> Para prestar los servicios solicitados.</li>
              <li><strong className="text-white">Consentimiento:</strong> Para el envío de comunicaciones comerciales.</li>
              <li><strong className="text-white">Interés legítimo:</strong> Para mejorar nuestros servicios y seguridad.</li>
              <li><strong className="text-white">Obligación legal:</strong> Para cumplir con la normativa aplicable.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Servicios de Terceros</h2>
            <p className="mb-4">Utilizamos los siguientes proveedores de servicios:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong className="text-white">Firebase (Google):</strong> Autenticación, base de datos y almacenamiento.{' '}
                <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">
                  Política de privacidad
                </a>
              </li>
              <li>
                <strong className="text-white">Stripe:</strong> Procesamiento de pagos.{' '}
                <a href="https://stripe.com/es/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">
                  Política de privacidad
                </a>
              </li>
              <li>
                <strong className="text-white">Google Analytics:</strong> Análisis de uso de la aplicación.{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">
                  Política de privacidad
                </a>
              </li>
              <li>
                <strong className="text-white">Firebase Crashlytics:</strong> Detección y corrección de errores.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Conservación de Datos</h2>
            <p>
              Sus datos personales se conservarán mientras mantenga su cuenta activa. Una vez solicite la eliminación de su cuenta,
              procederemos a eliminar sus datos en un plazo máximo de 30 días, salvo aquellos que debamos conservar por obligaciones legales.
            </p>
          </section>

          <section>
            <h2 id="delete-account" className="text-xl font-semibold text-white mb-4">7. Derechos del Usuario (Eliminación de cuenta)</h2>
            <p className="mb-4">De acuerdo con el RGPD, usted tiene derecho a:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-white">Acceso:</strong> Solicitar información sobre sus datos personales.</li>
              <li><strong className="text-white">Rectificación:</strong> Corregir datos inexactos o incompletos.</li>
              <li><strong className="text-white">Supresión:</strong> Solicitar la eliminación de sus datos ("derecho al olvido").</li>
              <li><strong className="text-white">Limitación:</strong> Restringir el tratamiento de sus datos.</li>
              <li><strong className="text-white">Portabilidad:</strong> Recibir sus datos en formato estructurado.</li>
              <li><strong className="text-white">Oposición:</strong> Oponerse al tratamiento de sus datos.</li>
            </ul>
            <p className="mt-4">
              Para ejercer estos derechos, contacte con nosotros en:{' '}
              <a href="mailto:navora.citas@gmail.com" className="text-amber-400 hover:text-amber-300">
                navora.citas@gmail.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Seguridad</h2>
            <p>
              Implementamos medidas técnicas y organizativas para proteger sus datos personales, incluyendo:
              encriptación de contraseñas, conexiones seguras (HTTPS), y acceso restringido a los datos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Menores de Edad</h2>
            <p>
              Nuestros servicios están dirigidos a mayores de 16 años. Los menores de 16 años necesitan
              el consentimiento de sus padres o tutores legales para utilizar la aplicación.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">10. Cambios en la Política</h2>
            <p>
              Nos reservamos el derecho de modificar esta política de privacidad. Cualquier cambio será
              notificado a través de la aplicación o por correo electrónico.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">11. Contacto y Reclamaciones</h2>
            <p className="mb-4">
              Para cualquier consulta sobre esta política, contacte con nosotros en:{' '}
              <a href="mailto:navora.citas@gmail.com" className="text-amber-400 hover:text-amber-300">
                navora.citas@gmail.com
              </a>
            </p>
            <p>
              Si considera que sus derechos no han sido atendidos correctamente, puede presentar una reclamación
              ante la Agencia Española de Protección de Datos (AEPD):{' '}
              <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">
                www.aepd.es
              </a>
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} Reserberry. Todos los derechos reservados.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;













